/**
 * ShellAppContext — single source of truth for the production shell app context.
 *
 * Provides one initialised instance of each transport client and the session
 * fields required by launch-critical pages.  Consumers must never call
 * page-level client factories directly when inside the shell.
 *
 * Fields:
 *   routeShopId        — shop id used in /app/:shopId/* routes
 *   gatewayToken       — installation token (used for gateway routing)
 *   prestashopShopId   — PrestaShop numeric shop id (stub: null until plugin lane extended)
 *   adminLanguageId    — current authenticated admin language ID
 *   domain             — storefront domain from the plugin bootstrap context
 *   storefrontUrl      — full storefront URL from the plugin bootstrap context
 *   gatewayAccessToken — JWT for gateway API requests
 *   bridgeClient       — BridgeClient instance (created once per shell mount)
 *   gatewayClient      — gateway search client (null when no valid session)
 *   runtime            — product-neutral admin command runtime
 *   searchSettingsClient — V2 editor port backed by the selected runtime
 *   productProfile     — selected cloud/plugin product composition profile
 *   capabilities                — feature capability map (minimal stub for now)
 *   compatibility               — resolved product workflow modes
 *   onAuthFailure               — callback: clears session and triggers recovery UI
 *   onboardingWizardCompleted   — boolean | null from shell session (fail-open: null = unknown)
 *   wizardBypass                — boolean | null: true when wizard was intentionally skipped
 *   flowId                      — string | null: bootstrap correlation id for observability
 *   telemetryPolicy             — gateway-issued in-memory policy; never persisted
 *   telemetryPolicyResolved     — false until the authoritative policy request finishes
 *   withdrawTelemetry           — revokes demo telemetry authority before leaving the shell
 */
import { createContext } from 'react';

export const ShellAppContext = createContext({
  routeShopId: null,
  gatewayToken: null,
  prestashopShopId: null,
  adminLanguageId: null,
  domain: null,
  storefrontUrl: null,
  moduleVersion: null,
  gatewayAccessToken: null,
  bridgeClient: null,
  gatewayClient: null,
  runtime: null,
  searchSettingsClient: null,
  productProfile: null,
  capabilities: {},
  compatibility: null,
  onAuthFailure: null,
  onboardingWizardCompleted: null,
  wizardBypass: null,
  flowId: null,
  telemetryPolicy: null,
  telemetryPolicyResolved: false,
  withdrawTelemetry: async () => false,
});
